/**
 * Modifica manuale del risultato di una partita (Andrea 2026-09-15).
 *
 * - AvvisoRettifica: l'avviso sotto il risultato, per tutti, quando un admin
 *   ha corretto a mano punteggi o gol: perche', il risultato che dava il
 *   calcolo, chi e quando.
 * - RettificaModal: la finestra di presidente e admin per correggere il
 *   risultato o tornare a quello del calcolo. Mentre si scrivono i fantapunti
 *   i gol seguono le soglie del regolamento, finche' non li si cambia a mano.
 */

import { useState } from "react";
import {
  type PartitaItem,
  type RettificaItem,
  useRettificaPartita,
  useTogliRettifica,
} from "../../hooks/useFase5";
import { useRegolamento } from "../../hooks/useStagione";
import { fmtPunti, golDaSoglie, leggiIntero, leggiNumero } from "./rettifica";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

function messaggioErrore(e: unknown): string {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = err.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return tr("Valori non validi: controlla fantapunti, gol e motivo.");
  return err.message ?? tr("Errore");
}

function testoRisultato(
  golCasa: number,
  golTrasferta: number,
  fpCasa: number | null,
  fpTrasferta: number | null,
): string {
  const gol = tr("{{v1}} - {{v2}}", { v1: golCasa, v2: golTrasferta });
  return fpCasa === null || fpTrasferta === null
    ? gol
    : tr("{{v1}} ({{v2}} · {{v3}} FP)", {
        v1: gol,
        v2: fmtPunti(fpCasa),
        v3: fmtPunti(fpTrasferta),
      });
}

export function AvvisoRettifica({ rettifica }: { rettifica: RettificaItem }) {
  const quando = new Date(rettifica.created_at).toLocaleString(localeCorrente(), {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <div className="rounded-card border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-left space-y-0.5">
      <div className="font-bold text-warning">{tr("✎ Risultato modificato manualmente")}</div>
      <div className="text-fg break-words">
        <span className="text-fg-muted">{tr("Motivo:")}</span> {rettifica.motivazione}
      </div>
      <div className="text-fg-muted">
        Risultato del calcolo:{" "}
        <span className="font-mono text-fg">
          {testoRisultato(
            rettifica.calcolato_gol_casa,
            rettifica.calcolato_gol_trasferta,
            rettifica.calcolato_punteggio_casa,
            rettifica.calcolato_punteggio_trasferta,
          )}
        </span>
      </div>
      <div className="text-[10px] text-fg-muted">
        {rettifica.creata_da ? tr("{{v1}} · ", { v1: rettifica.creata_da }) : ""}
        {quando}
      </div>
    </div>
  );
}

interface RettificaModalProps {
  partita: PartitaItem;
  nomeCasa: string;
  nomeTrasferta: string;
  stagioneId: string | null;
  onClose: () => void;
}

const INPUT_CLS =
  "w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-accent";

export function RettificaModal({
  partita,
  nomeCasa,
  nomeTrasferta,
  stagioneId,
  onClose,
}: RettificaModalProps) {
  const { data: regolamento } = useRegolamento(stagioneId);
  const salva = useRettificaPartita();
  const togli = useTogliRettifica();

  const [fpCasa, setFpCasa] = useState(
    partita.punteggio_casa === null ? "" : fmtPunti(partita.punteggio_casa),
  );
  const [fpTrasferta, setFpTrasferta] = useState(
    partita.punteggio_trasferta === null ? "" : fmtPunti(partita.punteggio_trasferta),
  );
  const [golCasa, setGolCasa] = useState(String(partita.gol_casa));
  const [golTrasferta, setGolTrasferta] = useState(String(partita.gol_trasferta));
  // I gol seguono i fantapunti finche' non li si tocca.
  const [golAMano, setGolAMano] = useState(false);
  const [motivazione, setMotivazione] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [avviso, setAvviso] = useState<string | null>(null);

  const soglie = regolamento?.soglie_gol ?? [];

  function cambiaFantapunti(lato: "casa" | "trasferta", valore: string) {
    (lato === "casa" ? setFpCasa : setFpTrasferta)(valore);
    const n = leggiNumero(valore);
    if (golAMano || n === null || soglie.length === 0) return;
    (lato === "casa" ? setGolCasa : setGolTrasferta)(String(golDaSoglie(n, soglie)));
  }

  function cambiaGol(lato: "casa" | "trasferta", valore: string) {
    (lato === "casa" ? setGolCasa : setGolTrasferta)(valore);
    setGolAMano(true);
  }

  const punteggioCasa = leggiNumero(fpCasa);
  const punteggioTrasferta = leggiNumero(fpTrasferta);
  const golCasaN = leggiIntero(golCasa);
  const golTrasfertaN = leggiIntero(golTrasferta);
  const motivo = motivazione.trim();
  const inCorso = salva.isPending || togli.isPending;
  const valido =
    punteggioCasa !== null &&
    punteggioTrasferta !== null &&
    golCasaN !== null &&
    golTrasfertaN !== null &&
    motivo.length >= 3;

  async function conferma() {
    if (
      punteggioCasa === null ||
      punteggioTrasferta === null ||
      golCasaN === null ||
      golTrasfertaN === null ||
      motivo.length < 3
    )
      return;
    setErrore(null);
    try {
      const esito = await salva.mutateAsync({
        partitaId: partita.id,
        punteggio_casa: punteggioCasa,
        punteggio_trasferta: punteggioTrasferta,
        gol_casa: golCasaN,
        gol_trasferta: golTrasfertaN,
        motivazione: motivo,
      });
      if (esito.avviso) setAvviso(esito.avviso);
      else onClose();
    } catch (e) {
      setErrore(messaggioErrore(e));
    }
  }

  async function tornaAlCalcolo() {
    if (!confirm(tr("Togliere la modifica manuale? La partita torna al risultato del calcolo."))) {
      return;
    }
    setErrore(null);
    try {
      await togli.mutateAsync(partita.id);
      onClose();
    } catch (e) {
      setErrore(messaggioErrore(e));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rettifica-titolo"
    >
      <div className="bg-bg border border-accent/30 rounded-xl max-w-md w-full shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="bg-primary-deep px-5 py-3 border-b border-accent/30">
          <h3 id="rettifica-titolo" className="font-display text-xl text-accent">
            {tr("Modifica risultato")}
          </h3>
          <p className="text-xs text-fg-muted truncate">
            {nomeCasa} – {nomeTrasferta}
          </p>
        </div>

        {avviso ? (
          <div className="p-5 space-y-3">
            <p className="text-sm font-bold text-success">{tr("✓ Risultato modificato")}</p>
            <p className="text-sm text-warning">{avviso}</p>
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className="btn-primary text-sm">
                {tr("Chiudi")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-5 space-y-4">
              <div className="text-xs text-fg-muted space-y-0.5">
                <div>
                  Risultato attuale:{" "}
                  <span className="font-mono text-fg">
                    {testoRisultato(
                      partita.gol_casa,
                      partita.gol_trasferta,
                      partita.punteggio_casa,
                      partita.punteggio_trasferta,
                    )}
                  </span>
                </div>
                {partita.rettifica && (
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span>
                      Risultato del calcolo:{" "}
                      <span className="font-mono text-fg">
                        {testoRisultato(
                          partita.rettifica.calcolato_gol_casa,
                          partita.rettifica.calcolato_gol_trasferta,
                          partita.rettifica.calcolato_punteggio_casa,
                          partita.rettifica.calcolato_punteggio_trasferta,
                        )}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={tornaAlCalcolo}
                      disabled={inCorso}
                      title={tr("Togli la modifica manuale e torna al risultato del calcolo")}
                      className="text-danger font-bold underline underline-offset-2 hover:opacity-80 disabled:opacity-50"
                    >
                      {togli.isPending ? "…" : tr("↺ Ripristina")}
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 gap-y-2 items-center">
                <span />
                <span className="text-xs font-bold text-center truncate">{nomeCasa}</span>
                <span className="text-xs font-bold text-center truncate">{nomeTrasferta}</span>

                <span className="text-xs text-fg-muted">{tr("Fantapunti")}</span>
                <input
                  aria-label={tr("Fantapunti {{v1}}", { v1: nomeCasa })}
                  inputMode="decimal"
                  value={fpCasa}
                  onChange={(e) => cambiaFantapunti("casa", e.target.value)}
                  className={INPUT_CLS}
                />
                <input
                  aria-label={tr("Fantapunti {{v1}}", { v1: nomeTrasferta })}
                  inputMode="decimal"
                  value={fpTrasferta}
                  onChange={(e) => cambiaFantapunti("trasferta", e.target.value)}
                  className={INPUT_CLS}
                />

                <span className="text-xs text-fg-muted">{tr("Gol")}</span>
                <input
                  aria-label={tr("Gol {{v1}}", { v1: nomeCasa })}
                  inputMode="numeric"
                  value={golCasa}
                  onChange={(e) => cambiaGol("casa", e.target.value)}
                  className={INPUT_CLS}
                />
                <input
                  aria-label={tr("Gol {{v1}}", { v1: nomeTrasferta })}
                  inputMode="numeric"
                  value={golTrasferta}
                  onChange={(e) => cambiaGol("trasferta", e.target.value)}
                  className={INPUT_CLS}
                />
              </div>
              <p className="text-[11px] text-fg-muted">
                {soglie.length === 0
                  ? tr("Soglie gol non disponibili: scrivi i gol a mano.")
                  : golAMano
                    ? tr("Gol scritti a mano.")
                    : tr(
                        "I gol seguono i fantapunti con le soglie del regolamento; puoi cambiarli a mano.",
                      )}
              </p>

              <label className="block">
                <span className="text-xs text-fg-muted">{tr("Motivo (lo vedono tutti)")}</span>
                <textarea
                  rows={2}
                  value={motivazione}
                  onChange={(e) => setMotivazione(e.target.value)}
                  placeholder={tr("Es. voto di Rossi sbagliato alla fonte")}
                  className="mt-1 w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </label>

              <p className="text-[11px] text-fg-muted">
                {tr(
                  tr(
                    "Vale anche per la classifica e resta applicata se la giornata viene ricalcolata. Le scommesse già pagate non vengono ricalcolate.",
                  ),
                )}
              </p>
              {errore && <p className="text-xs text-danger">{errore}</p>}
            </div>

            <div className="bg-primary-deep/40 px-5 py-3 border-t border-primary-soft flex items-center justify-between gap-2">
              <button type="button" onClick={onClose} className="btn-ghost text-sm">
                {tr("Annulla")}
              </button>
              <button
                type="button"
                onClick={conferma}
                disabled={!valido || inCorso}
                className="btn-primary text-sm disabled:opacity-50"
              >
                {salva.isPending ? "…" : tr("Salva modifica")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
